"use client";

import {
  m as motion,
  useReducedMotion,
  useInView,
  useMotionValue,
  useTransform,
  animate,
  type MotionValue,
} from "framer-motion";
import { useEffect, useRef, type ReactNode } from "react";
import {
  Lightbulb,
  Target,
  Code2,
  Rocket,
  RefreshCw,
  type LucideIcon,
} from "lucide-react";

interface MvpPipelineProps {
  isItalian: boolean;
}

type Step = {
  icon: LucideIcon;
  title: string;
  description: ReactNode;
  duration: string;
};

const EASE = [0.16, 1, 0.3, 1] as const;
const STEP_STAGGER = 0.18;

// ESPERIMENTO LAYOUT (Round 6 -> P): processo come CIRCUITO OVALE chiuso (desktop
// lg+). 5 nodi su un anello a forma di stadium; una luce ciano gira in senso
// ORARIO in loop continuo e accende i nodi al passaggio. Tutto deriva da UN SOLO
// `progress` motionValue (0->1 in loop):
//   - flusso luminoso = `strokeDashoffset` di un path-highlight che corre sul
//     path dell'ovale (idioma classico circuit-flow, un solo elemento, leggero);
//   - glow di ogni nodo = opacity con picco quando progress ~ posizione del nodo
//     lungo l'anello (stessa sorgente -> sync garantito).
// Gate: `loopActive = inViewLoop && !reduce` (useInView SENZA once). Fuori
// schermo / reduced-motion -> circuito STATICO completamente acceso, nessun loop.
// Solo transform/opacity (qui: strokeDashoffset + opacity). Niente layout shift.
// Mobile (< lg): fallback alla lista verticale dei 5 step con binario verticale.

// Durata di un giro completo dell'anello (s).
const LOOP_DURATION = 7;

// --- Geometria SVG dell'ovale (viewBox 0 0 1000 440) ----------------------
// Forma a "stadium": 3 nodi sul lato alto (01,02,03), 2 nodi sul lato basso
// spostati VERSO L'INTERNO (05 tra 01 e 02, 04 tra 02 e 03) per un ovale piu'
// bilanciato. Gli archi laterali bulgiano verso l'ESTERNO (oltre x=850 a destra,
// oltre x=150 a sinistra) e scendono in diagonale ai nodi bassi interni.
const VB_W = 1000;
const VB_H = 440;
const TOP_LX = 150; // 01 Analisi (alto-sx)
const TOP_CX = 500; // 02 Perimetro (alto-centro)
const TOP_RX = 850; // 03 Sviluppo (alto-dx)
const BOT_LX = 325; // 05 Iterazione (basso, tra 01 e 02)
const BOT_RX = 675; // 04 Lancio (basso, tra 02 e 03)
const TOP_Y = 90; // riga alta
const BOTTOM_Y = 350; // riga bassa
// Quanto gli archi laterali sporgono oltre i nodi estremi (verso l'esterno).
const ARC_BULGE = 150;
const RIGHT_BULGE_X = TOP_RX + ARC_BULGE; // 970 (apice arco destro)
const LEFT_BULGE_X = TOP_LX - ARC_BULGE; // 30 (apice arco sinistro)

// Path percorso in senso ORARIO partendo da Analisi (alto-sx):
//   top: 01(150,90) -> 02(500,90) -> 03(850,90)
//   arco destro (bulge a dx, diagonale): 03(850,90) -> 04(675,350)
//   bottom: 04(675,350) -> 05(325,350)
//   arco sinistro (bulge a sx, diagonale): 05(325,350) -> 01(150,90)
// Uso curve cubiche per archi morbidi che sporgono verso l'esterno.
const OVAL_PATH =
  `M ${TOP_LX} ${TOP_Y} ` +
  `L ${TOP_RX} ${TOP_Y} ` +
  `C ${RIGHT_BULGE_X} ${TOP_Y}, ${RIGHT_BULGE_X} ${BOTTOM_Y}, ${BOT_RX} ${BOTTOM_Y} ` +
  `L ${BOT_LX} ${BOTTOM_Y} ` +
  `C ${LEFT_BULGE_X} ${BOTTOM_Y}, ${LEFT_BULGE_X} ${TOP_Y}, ${TOP_LX} ${TOP_Y} Z`;

// Lunghezza approssimata dei segmenti per piazzare i 5 nodi alla loro frazione
// lungo l'anello (e tenere glow/label in sync col flusso luminoso). Le cubiche
// sono approssimate con la corda verticale * fattore.
const LEN_TOP = TOP_RX - TOP_LX; // 700 (01 -> 02 -> 03)
const LEN_BOTTOM = BOT_RX - BOT_LX; // 350 (04 -> 05), nodi bassi interni
// Stima della lunghezza di un arco cubico laterale (DIAGONALE: dal nodo alto
// estremo al nodo basso interno). La corda e' diagonale; le tangenti orizzontali
// (bulge ARC_BULGE) curvano l'arco oltre la corda. Fattore 1.47 calibrato
// campionando la cubica reale (~461 per corda ~313), cosi' NODE_FRACTION combacia
// con la posizione reale della luce lungo il path -> glow/label dei nodi 03/04/05
// restano in sync al passaggio.
const LEN_ARC = Math.hypot(TOP_RX - BOT_RX, BOTTOM_Y - TOP_Y) * 1.47;
const TOTAL_LEN = LEN_TOP + LEN_ARC + LEN_BOTTOM + LEN_ARC;

// Posizione (frazione 0..1 lungo l'anello orario, 0 = Analisi) di ogni nodo.
// 01 a 0 ; 02 a meta' del top ; 03 a fine top ; 04 a fine arco destro ; 05 a
// fine bottom. Il ritorno (arco sinistro) chiude il giro verso 01.
const NODE_FRACTION = [
  0, // 01 Analisi (alto-sx)
  LEN_TOP / 2 / TOTAL_LEN, // 02 Perimetro (alto-centro)
  LEN_TOP / TOTAL_LEN, // 03 Sviluppo (alto-dx)
  (LEN_TOP + LEN_ARC) / TOTAL_LEN, // 04 Lancio (basso-dx)
  (LEN_TOP + LEN_ARC + LEN_BOTTOM) / TOTAL_LEN, // 05 Iterazione (basso-sx)
];

// Posizione dei 5 nodi in % del box (per piazzare le ICONE in assoluto sopra
// l'SVG, con il CENTRO ICONA esattamente sul punto del path) + numero d'ordine
// del circuito + indicazione se il testo va SOPRA (nodi alti) o SOTTO (bassi).
// Il blocco di testo e' CENTRATO orizzontalmente sull'icona: con i nodi bassi
// tornati agli ANGOLI (05 sotto 01, 04 sotto 03) i blocchi centrati hanno spazio
// e NON collidono (Sviluppo/Perimetro e Lancio/Iterazione restano distanziati).
type NodePos = {
  xPct: number;
  yPct: number;
  place: "top" | "bottom";
  num: string;
};
const NODE_POS: NodePos[] = [
  { xPct: (TOP_LX / VB_W) * 100, yPct: (TOP_Y / VB_H) * 100, place: "top", num: "01" }, // Analisi
  { xPct: (TOP_CX / VB_W) * 100, yPct: (TOP_Y / VB_H) * 100, place: "top", num: "02" }, // Perimetro
  { xPct: (TOP_RX / VB_W) * 100, yPct: (TOP_Y / VB_H) * 100, place: "top", num: "03" }, // Sviluppo
  { xPct: (BOT_RX / VB_W) * 100, yPct: (BOTTOM_Y / VB_H) * 100, place: "bottom", num: "04" }, // Lancio
  { xPct: (BOT_LX / VB_W) * 100, yPct: (BOTTOM_Y / VB_H) * 100, place: "bottom", num: "05" }, // Iterazione
];

/**
 * Pipeline animata del processo MVP (5 fasi) come CIRCUITO OVALE.
 *
 * Desktop: i 5 nodi sono i vertici di uno stadium; un singolo `progress`
 * motionValue (0->1 in loop) pilota sia il flusso luminoso (strokeDashoffset di
 * un path-highlight che corre orario sul path) sia il glow di ogni nodo (opacity
 * con picco quando progress ~ NODE_FRACTION[i]). Il testo dei nodi alti sta SOPRA
 * la riga alta, quello dei nodi bassi SOTTO la riga bassa, per non intasare il
 * centro dell'ovale.
 *
 * Mobile: fallback alla lista verticale dei 5 step con binario + scintilla.
 *
 * Gate: `loopActive = inViewLoop && !reduce` (useInView SENZA once). Fuori
 * schermo / reduced-motion -> circuito statico tutto acceso, nessun loop.
 */
export function MvpPipeline({ isItalian }: MvpPipelineProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  // Reveal one-shot allo scroll.
  const inView = useInView(ref, { once: true, margin: "-60px" });
  // Gate del loop: continuo, si spegne quando la sezione esce dal viewport.
  const inViewLoop = useInView(ref, { margin: "-80px" });
  const show = inView || !!reduce;
  const loopActive = inViewLoop && !reduce;

  // Progress condiviso 0->1: la sorgente unica di verita' per tutta l'animazione.
  const progress = useMotionValue(0);

  useEffect(() => {
    if (!loopActive) {
      progress.set(0);
      return;
    }
    const controls = animate(progress, 1, {
      duration: LOOP_DURATION,
      ease: "linear",
      repeat: Infinity,
      repeatType: "loop",
    });
    return () => controls.stop();
  }, [loopActive, progress]);

  // Helper: evidenzia in ciano una keyword del processo dentro la descrizione.
  const hl = (text: ReactNode) => (
    <span className="text-primary font-medium">{text}</span>
  );

  const steps: Step[] = isItalian
    ? [
        {
          icon: Lightbulb,
          title: "Analisi",
          description: (
            <>
              Analizziamo insieme la tua idea, il mercato target e definiamo{" "}
              {hl("obiettivi chiari e misurabili")}.
            </>
          ),
          duration: "Prima della firma",
        },
        {
          icon: Target,
          title: "Perimetro e piano",
          description: (
            <>
              Definiamo le {hl("feature core")} del MVP, eliminiamo il superfluo
              e creiamo una roadmap realistica.
            </>
          ),
          duration: "Prima della firma",
        },
        {
          icon: Code2,
          title: "Sviluppo",
          description: (
            <>
              Sviluppo {hl("iterativo")} con aggiornamenti regolari. Vedi il tuo
              prodotto prendere forma.
            </>
          ),
          duration: "3-5 settimane",
        },
        {
          icon: Rocket,
          title: "Lancio",
          description: (
            <>
              Deploy su {hl("infrastruttura cloud scalabile")}. Il tuo MVP è live
              e pronto per i primi utenti.
            </>
          ),
          duration: "1 settimana",
        },
        {
          icon: RefreshCw,
          title: "Iterazione",
          description: (
            <>
              Raccogli {hl("feedback")}, analizza i dati e pianifichiamo insieme
              le prossime iterazioni.
            </>
          ),
          duration: "Continuo",
        },
      ]
    : [
        {
          icon: Lightbulb,
          title: "Discovery",
          description: (
            <>
              We analyze your idea, target market, and define{" "}
              {hl("clear, measurable goals")} together.
            </>
          ),
          duration: "Before signing",
        },
        {
          icon: Target,
          title: "Scope & Planning",
          description: (
            <>
              We define the MVP {hl("core features")}, remove noise, and create a
              realistic roadmap.
            </>
          ),
          duration: "Before signing",
        },
        {
          icon: Code2,
          title: "Build",
          description: (
            <>
              {hl("Iterative")} development with regular updates. You see your
              product taking shape.
            </>
          ),
          duration: "3-5 weeks",
        },
        {
          icon: Rocket,
          title: "Launch",
          description: (
            <>
              Deployment on {hl("scalable cloud infrastructure")}. Your MVP is
              live and ready for first users.
            </>
          ),
          duration: "1 week",
        },
        {
          icon: RefreshCw,
          title: "Iterate",
          description: (
            <>
              Collect {hl("feedback")}, analyze data, and plan the next
              iterations together.
            </>
          ),
          duration: "Ongoing",
        },
      ];

  const n = steps.length; // 5

  // TOP padding dimezzato (era pt-28/36/44); bottom padding generoso invariato.
  // Sotto l'intro, separazione ampia verso i nodi alti dell'ovale (mb maggiore).
  return (
    <section id="mvp-processo" className="scroll-mt-24 pt-14 md:pt-18 lg:pt-22 pb-28 md:pb-36 lg:pb-44">
      <div className="container-wide" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={show ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-left mb-32 lg:mb-44 max-w-2xl lg:max-w-4xl"
        >
          <span className="font-mono text-label text-primary mb-3 block">
            {isItalian ? "Come lavoriamo" : "How we work"}
          </span>
          <h2 className="font-display text-h2 font-bold mb-4">
            {isItalian ? "Il processo" : "The process"}
          </h2>
          {/* Sottotitolo su UNA riga su desktop (lg+): copy accorciato + blocco
              intro piu' largo (max-w-4xl) + text-balance disattivato su lg cosi'
              non viene spezzato a meta'. Su schermi piccoli puo' andare a capo. */}
          <p className="text-body-lg text-muted-foreground text-balance lg:text-pretty">
            {isItalian ? (
              <>
                Una pipeline in{" "}
                <span className="text-primary">5 fasi</span>{" "}
                che ti porta dall&apos;idea al{" "}
                <span className="text-primary">prodotto live</span>, con{" "}
                <span className="text-primary">tempi chiari</span>.
              </>
            ) : (
              <>
                A <span className="text-primary">5-step</span>{" "}
                pipeline that takes you from idea to{" "}
                <span className="text-primary">live product</span>, with{" "}
                <span className="text-primary">clear timing</span>.
              </>
            )}
          </p>
        </motion.div>

        {/* Desktop: CIRCUITO OVALE */}
        <div className="hidden lg:block">
          <OvalCircuit
            steps={steps}
            progress={progress}
            loopActive={loopActive}
            reduce={!!reduce}
            show={show}
          />
        </div>

        {/* Mobile/tablet: pipeline verticale (fallback) */}
        <div className="lg:hidden relative max-w-xl">
          {/* Binario di sfondo: passa per la meta' verticale delle icone. */}
          <div
            className="absolute top-3 bottom-3 w-[2px] rounded-full bg-[hsl(var(--border))]"
            style={{ left: 28 }}
          />
          {/* Binario che si riempie */}
          <motion.div
            initial={false}
            animate={{ scaleY: show ? 1 : 0 }}
            transition={{ duration: reduce ? 0 : 1.6, ease: EASE }}
            style={{ transformOrigin: "top center", left: 28 }}
            className="absolute top-3 bottom-3 w-[2px] origin-top rounded-full bg-gradient-to-b from-primary/70 to-primary-glow/70"
          />
          {/* Scintilla "elettrica" verticale in loop (transform/y only) */}
          {loopActive && (
            <div
              className="absolute top-3 bottom-3 w-[2px] overflow-visible pointer-events-none"
              style={{ left: 28 }}
            >
              <motion.div
                className="absolute left-[-0.5px] top-0 w-[3px] h-16 rounded-full bg-primary opacity-80 blur-[1px]"
                style={{
                  maskImage:
                    "linear-gradient(180deg, transparent, #000 35%, #000 65%, transparent)",
                  WebkitMaskImage:
                    "linear-gradient(180deg, transparent, #000 35%, #000 65%, transparent)",
                }}
                animate={{ y: ["-4rem", "calc(100% + 4rem)"] }}
                transition={{
                  duration: LOOP_DURATION,
                  ease: "linear",
                  repeat: Infinity,
                }}
              />
            </div>
          )}

          <div className="space-y-7">
            {steps.map((step, i) => (
              <motion.div
                key={step.title}
                initial={false}
                animate={show ? { opacity: 1, x: 0 } : { opacity: 0, x: 16 }}
                transition={{
                  duration: reduce ? 0 : 0.5,
                  delay: reduce ? 0 : 0.2 + i * STEP_STAGGER,
                  ease: EASE,
                }}
                className="flex gap-4 items-start"
              >
                <div className="relative shrink-0 z-10">
                  <NodeGlow
                    progress={progress}
                    i={i}
                    loopActive={loopActive}
                    reduce={!!reduce}
                  />
                  <motion.div
                    initial={false}
                    animate={show ? { scale: 1 } : { scale: reduce ? 1 : 0.6 }}
                    transition={{
                      duration: reduce ? 0 : 0.4,
                      delay: reduce ? 0 : 0.25 + i * STEP_STAGGER,
                      ease: EASE,
                    }}
                    className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center shadow-[0_8px_24px_hsl(var(--primary)/0.25)]"
                  >
                    <step.icon className="w-7 h-7 text-primary-foreground" />
                    {/* Badge numero d'ordine del circuito (coerente con l'ovale). */}
                    <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-surface text-[11px] font-bold text-primary shadow-[0_2px_8px_hsl(var(--primary)/0.25)] ring-1 ring-primary/30">
                      {NODE_POS[i].num}
                    </span>
                  </motion.div>
                </div>
                <div className="pt-1 min-w-0 text-left">
                  {/* NOME fase in alto (si illumina con l'icona via opacity) */}
                  <NodeLabel
                    progress={progress}
                    i={i}
                    loopActive={loopActive}
                    show={show}
                    as="mobile"
                  >
                    {step.title}
                  </NodeLabel>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                  {/* Tempistica sotto */}
                  <span className="font-mono text-xs text-primary mt-2 block">
                    {step.duration}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Circuito ovale (desktop). Disegna l'anello come UN path SVG (stadium): un
 * tratto debole = rotaia statica, un tratto luminoso = luce che scorre in senso
 * ORARIO (strokeDashoffset derivato dal progress condiviso). I 5 nodi (icona +
 * glow) sono posizionati in assoluto sopra l'SVG ai loro punti del path. Il testo
 * dei nodi alti sta SOPRA la riga alta, dei nodi bassi SOTTO la riga bassa.
 */
function OvalCircuit({
  steps,
  progress,
  loopActive,
  reduce,
  show,
}: {
  steps: Step[];
  progress: MotionValue<number>;
  loopActive: boolean;
  reduce: boolean;
  show: boolean;
}) {
  // Lunghezza unitaria del dash: usiamo pathLength=1 cosi' dash/offset sono in
  // frazioni 0..1 (indipendente dalla lunghezza reale del path).
  // Segmento luminoso = ~14% dell'anello; scorre orario via offset 0 -> -1.
  const DASH = 0.07;
  const GAP = 1 - DASH;
  // offset negativo fa avanzare il dash nel verso di disegno del path (orario).
  const dashOffset = useTransform(progress, [0, 1], [0, -1]);
  // Punta a freccia: segue la TESTA della luce. Con dasharray `DASH GAP` e
  // dashoffset -progress, il bordo d'attacco del tratto luminoso (nel verso di
  // disegno = orario) sta alla frazione `progress` lungo il path. L'elemento
  // freccia usa offset-path = stesso path e offset-distance = progress*100%, con
  // auto-rotate (CSS offset-rotate: auto) per orientarsi nel verso di marcia.
  // Solo transform/opacity. La freccia esiste solo quando il loop e' attivo.
  // La freccia sta sulla TESTA (bordo d'attacco) della luce: il tratto luminoso
  // occupa [progress, progress+DASH] nel verso orario, quindi la testa e' a
  // progress+DASH (mod 1).
  const arrowDistance = useTransform(
    progress,
    (p) => `${((p + DASH) % 1) * 100}%`,
  );

  return (
    <div className="relative w-full max-w-4xl">
      {/* Riserva verticale per il testo sopra/sotto + l'ovale al centro.
          Aspect-ratio fisso = niente layout shift. Margini interni laterali per
          dare respiro ai blocchi di testo dei nodi estremi. */}
      <div className="relative ml-0 mr-[6%] w-auto" style={{ aspectRatio: `${VB_W} / ${VB_H}` }}>
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          className="absolute inset-0 h-full w-full overflow-visible"
          fill="none"
          aria-hidden="true"
        >
          {/* Rotaia statica (anello completo, debole). */}
          <motion.path
            d={OVAL_PATH}
            stroke="hsl(var(--border))"
            strokeWidth={2}
            strokeLinecap="round"
            pathLength={1}
            initial={false}
            animate={{ pathLength: show ? 1 : 0 }}
            transition={{ duration: reduce ? 0 : 1.6, ease: EASE }}
          />
          {/* Anello acceso debole (reveal) per dare "vita" anche da fermo. */}
          <motion.path
            d={OVAL_PATH}
            stroke="hsl(var(--primary))"
            strokeOpacity={reduce ? 0.9 : 0.28}
            strokeWidth={2}
            strokeLinecap="round"
            pathLength={1}
            initial={false}
            animate={{ pathLength: show ? 1 : 0 }}
            transition={{ duration: reduce ? 0 : 1.6, ease: EASE }}
          />
          {/* Luce che scorre ORARIA: tratto luminoso che corre sul path via
              strokeDashoffset. Solo quando il loop e' attivo. */}
          {loopActive && (
            <motion.path
              d={OVAL_PATH}
              stroke="hsl(var(--primary))"
              strokeWidth={4}
              strokeLinecap="round"
              pathLength={1}
              style={{
                strokeDasharray: `${DASH} ${GAP}`,
                strokeDashoffset: dashOffset,
                filter: "drop-shadow(0 0 6px hsl(var(--primary)))",
              }}
            />
          )}
          {/* Punta a freccia sulla TESTA della luce: triangolino che segue il
              path via offset-path/offset-distance (sync col progress), orientato
              nel verso di marcia (offset-rotate: auto). Disegnato attorno
              all'origine locale; punta verso +X, che con auto-rotate diventa il
              verso di avanzamento lungo il path. Solo transform. */}
          {loopActive && (
            <motion.polygon
              points="9,0 -7,-7 -7,7"
              fill="hsl(var(--primary))"
              style={{
                offsetPath: `path('${OVAL_PATH}')`,
                offsetDistance: arrowDistance,
                offsetRotate: "auto",
                filter: "drop-shadow(0 0 5px hsl(var(--primary)))",
              }}
            />
          )}
        </svg>

        {/* Nodi: il CENTRO ICONA e' ancorato esattamente sul punto del path
            (translate(-50%,-50%) sul wrapper). Il blocco di testo galleggia in
            assoluto SOPRA (nodi alti) o SOTTO (nodi bassi) l'icona, fuori
            dall'anello, senza spostare il centro icona. */}
        {steps.map((step, i) => {
          const pos = NODE_POS[i];
          return (
            <div
              key={step.title}
              className="absolute"
              style={{
                left: `${pos.xPct}%`,
                top: `${pos.yPct}%`,
                transform: "translate(-50%, -50%)",
              }}
            >
              <OvalNode
                step={step}
                i={i}
                place={pos.place}
                num={pos.num}
                progress={progress}
                loopActive={loopActive}
                reduce={reduce}
                show={show}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Singolo nodo dell'ovale. L'ICONA e' l'elemento ancorato: il suo centro sta
 * esattamente sul punto del path (il wrapper esterno applica translate -50%/-50%),
 * quindi l'icona STRADDLE la linea dell'ovale come nei nodi abbonamento. Un badge
 * circolare con il numero d'ordine (01..05) sta in alto-a-destra dell'icona. Il
 * blocco di testo (nome che si illumina + descrizione con keyword ciano +
 * tempistica) galleggia in ASSOLUTO ed e' CENTRATO orizzontalmente sull'icona
 * (text-center, left-1/2 + translate -50%): SOPRA la linea per i nodi alti
 * (place="top"), SOTTO per i bassi (place="bottom"). Con i nodi bassi agli angoli
 * (05 sotto 01, 04 sotto 03) i blocchi centrati hanno spazio e non collidono.
 */
function OvalNode({
  step,
  i,
  place,
  num,
  progress,
  loopActive,
  reduce,
  show,
}: {
  step: Step;
  i: number;
  place: "top" | "bottom";
  num: string;
  progress: MotionValue<number>;
  loopActive: boolean;
  reduce: boolean;
  show: boolean;
}) {
  // Il blocco testo e' posizionato in assoluto rispetto all'icona ancorata e
  // CENTRATO sul centro dell'icona (left-1/2 + -translate-x-1/2, text-center).
  // Verticale: nodi alti SOPRA (bottom-full + margine), bassi SOTTO (top-full).
  // Nodi alti: testo SOPRA, con min-height uguale per tutti cosi' i TITOLI si
  // allineano alla stessa altezza (blocco ancorato in basso; contenuto piu' corto
  // lascia spazio in fondo, ma il titolo resta in cima). Bassi: testo SOTTO.
  const vert =
    place === "top" ? "bottom-full mb-6 min-h-[10rem]" : "top-full mt-8";

  return (
    <div className="relative">
      {/* Blocco testo, fuori dall'anello, sulla colonna dell'icona (centrato
          sull'icona ma testo allineato a SINISTRA). Larghezza contenuta (w-48)
          -> niente collisioni e Sviluppo (03) non sfora a destra. */}
      <motion.div
        initial={false}
        animate={
          show
            ? { opacity: 1, y: 0 }
            : { opacity: 0, y: place === "top" ? -10 : 10 }
        }
        transition={{
          duration: reduce ? 0 : 0.5,
          delay: reduce ? 0 : 0.25 + i * STEP_STAGGER,
          ease: EASE,
        }}
        className={`absolute w-48 left-0 text-left ${vert}`}
      >
        <NodeLabel
          progress={progress}
          i={i}
          loopActive={loopActive}
          show={show}
          as="oval"
        >
          {step.title}
        </NodeLabel>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {step.description}
        </p>
        <span className="font-mono text-xs text-primary mt-2 block">
          {step.duration}
        </span>
      </motion.div>

      {/* Icona sul path con glow sincronizzato + badge numero. */}
      <div className="relative">
        <NodeGlow
          progress={progress}
          i={i}
          loopActive={loopActive}
          reduce={reduce}
        />
        <motion.div
          initial={false}
          animate={show ? { scale: 1 } : { scale: reduce ? 1 : 0.6 }}
          transition={{
            duration: reduce ? 0 : 0.4,
            delay: reduce ? 0 : 0.3 + i * STEP_STAGGER,
            ease: EASE,
          }}
          className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center shadow-[0_8px_24px_hsl(var(--primary)/0.25)]"
        >
          <step.icon className="w-7 h-7 text-primary-foreground" />
          {/* Badge numero d'ordine del circuito (top-right, stile abbonamento). */}
          <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-surface text-[11px] font-bold text-primary shadow-[0_2px_8px_hsl(var(--primary)/0.25)] ring-1 ring-primary/30">
            {num}
          </span>
        </motion.div>
      </div>
    </div>
  );
}

// Costruisce un impulso (base -> peak -> base) centrato su NODE_FRACTION[i],
// largo `2*half` lungo l'anello, con gestione del wrap a 0/1 (il nodo Analisi a
// frazione 0 si accende anche a fine giro, quando la luce sta tornando da
// Iterazione lungo l'arco sinistro). Usato da glow e label per restare in sync.
// Nota: hook (chiama useTransform) -> nome con prefisso `use`, chiamato sempre al
// top-level di NodeGlow/NodeLabel (regole degli hook rispettate).
function usePulse(progress: MotionValue<number>, i: number, base: number) {
  const center = NODE_FRACTION[i];
  const half = 0.07;
  return useTransform(progress, (p) => {
    // distanza circolare su [0,1)
    let d = Math.abs(p - center);
    d = Math.min(d, 1 - d);
    if (d >= half) return base;
    const t = 1 - d / half; // 0 al bordo, 1 al centro
    const s = t * t * (3 - 2 * t); // smoothstep
    return base + (1 - base) * s;
  });
}

/**
 * Glow del nodo i: opacity con picco a 1 quando la luce passa sul nodo (progress
 * ~ NODE_FRACTION[i]); a riposo basso. Reduced-motion: glow medio statico. Fuori
 * loop: spento. Solo opacity.
 */
function NodeGlow({
  progress,
  i,
  loopActive,
  reduce,
}: {
  progress: MotionValue<number>;
  i: number;
  loopActive: boolean;
  reduce: boolean;
}) {
  const opacity = usePulse(progress, i, 0.22);
  return (
    <motion.span
      aria-hidden="true"
      className="absolute -inset-3 rounded-3xl bg-primary/70 blur-xl"
      style={loopActive ? { opacity } : undefined}
      initial={false}
      animate={loopActive ? undefined : { opacity: reduce ? 0.45 : 0 }}
      transition={loopActive ? undefined : { duration: 0.3 }}
    />
  );
}

/**
 * NOME fase: opacity sincronizzata col glow (stesso progress). A riposo leggibile
 * ma "spento", picco a 1 al passaggio della luce.
 */
function NodeLabel({
  progress,
  i,
  loopActive,
  show,
  as,
  children,
}: {
  progress: MotionValue<number>;
  i: number;
  loopActive: boolean;
  show: boolean;
  as: "oval" | "mobile";
  children: ReactNode;
}) {
  const opacity = usePulse(progress, i, 0.6);
  const className =
    as === "oval"
      ? "font-display text-lg font-bold mb-2"
      : "font-display text-lg font-bold mb-1";
  return (
    <motion.h3
      className={className}
      style={loopActive ? { opacity } : undefined}
      initial={false}
      animate={loopActive ? undefined : { opacity: show ? 1 : 0 }}
      transition={loopActive ? undefined : { duration: 0.3 }}
    >
      {children}
    </motion.h3>
  );
}
